import { useAppQuery } from "../../../../../../../http/useAppQuery.ts";
import {
  ANIMATE_GENERATION_TYPE,
  BRIEF_TO_IMAGE_GENERATION_TYPE,
  COPY_AD_GENERATION_TYPE,
  EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE,
  ENHANCE_GENERATION_TYPE,
  EXPAND_GENERATION_TYPE,
  GENERATIVE_FILL_GENERATION_TYPE,
  IMAGE_TO_IMAGE_GENERATION_TYPE,
  STYLE_TRANSFER_GENERATION_TYPE,
  TEXT_TO_IMAGE_GENERATION_TYPE,
  UPSCALER_GENERATION_TYPE,
} from "../../../../../constants.ts";
import { GENERATION_RATIO_DISPLAY } from "../../../../../display.ts";
import type {
  DetailedContent,
  UpscalerGenerationParams,
} from "../../../../../types.ts";
import { TextItemSectionDetails } from "../../../../components/SectionDetails.tsx";
import { initImageColorInfluenceLabelMapping } from "../../../../CreateWorkspace/Tools/StyleTransferView/constants.ts";
import {
  CUSTOM_UPSCALE_IMAGINATION_STRENGTH_PRESET,
  type UpscaleImaginationStrengthPresets,
} from "../../../Tools/EditAsset/Upscale/types.ts";

export const ContentGenerationParametersDetails = ({
  content,
}: {
  content: DetailedContent;
}) => {
  // FIXME: We should display default value from preset if it's not set (as done with upscale type)
  switch (content.information.generation_parameters?.generation_type) {
    case TEXT_TO_IMAGE_GENERATION_TYPE:
      return content.information.generation_parameters.generation_ratio ? (
        <TextItemSectionDetails
          name="Image ratio"
          value={
            GENERATION_RATIO_DISPLAY[
              content.information.generation_parameters.generation_ratio
            ].label
          }
        />
      ) : null;
    case EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE:
      return null;
    case IMAGE_TO_IMAGE_GENERATION_TYPE:
      return (
        <>
          <TextItemSectionDetails
            name="Creativity preset"
            value={
              content.information.generation_parameters
                .creativity_strength_preset ?? "None"
            }
          />
          {content.information.generation_parameters.prompt_strength ? (
            <TextItemSectionDetails
              name="Creativity"
              value={
                Math.round(
                  content.information.generation_parameters.prompt_strength *
                    100,
                ) + "%"
              }
            />
          ) : null}
        </>
      );
    case STYLE_TRANSFER_GENERATION_TYPE:
      return (
        <>
          <TextItemSectionDetails
            name="Input type"
            value={
              content.information.generation_parameters
                .init_image_color_influence
                ? initImageColorInfluenceLabelMapping[
                    content.information.generation_parameters
                      .init_image_color_influence
                  ]
                : "None"
            }
          />
          {content.information.generation_parameters.colors_strength ? (
            <TextItemSectionDetails
              name="Input color"
              value={
                Math.round(
                  content.information.generation_parameters.colors_strength *
                    100,
                ) + "%"
              }
            />
          ) : null}
          {content.information.generation_parameters.structure_strength ? (
            <TextItemSectionDetails
              name="Input structure"
              value={
                Math.round(
                  content.information.generation_parameters.structure_strength *
                    100,
                ) + "%"
              }
            />
          ) : null}
          {content.information.generation_parameters.creativity ? (
            <TextItemSectionDetails
              name="Creativity"
              value={
                Math.round(
                  content.information.generation_parameters.creativity * 100,
                ) + "%"
              }
            />
          ) : null}
        </>
      );
    case UPSCALER_GENERATION_TYPE:
      return (
        <UpscaleGenerationParameters
          generationParams={content.information.generation_parameters}
        />
      );
    case GENERATIVE_FILL_GENERATION_TYPE:
      return content.information.generation_parameters.prompt_strength ? (
        <TextItemSectionDetails
          name="Prompt strength"
          value={String(
            content.information.generation_parameters.prompt_strength,
          )}
        />
      ) : null;
    case BRIEF_TO_IMAGE_GENERATION_TYPE:
      return content.information.generation_parameters.generation_ratio ? (
        <TextItemSectionDetails
          name="Image ratio"
          value={
            GENERATION_RATIO_DISPLAY[
              content.information.generation_parameters.generation_ratio
            ].label
          }
        />
      ) : null;
    case EXPAND_GENERATION_TYPE:
      return null;
    case ENHANCE_GENERATION_TYPE:
      return null;
    case ANIMATE_GENERATION_TYPE:
      return null;
    case COPY_AD_GENERATION_TYPE:
      return null;
    case undefined:
      return null;
  }
};

const UpscaleGenerationParameters = ({
  generationParams,
}: {
  generationParams: UpscalerGenerationParams;
}) => {
  const { data: upscaleImaginationStrengthPresets } =
    useAppQuery<UpscaleImaginationStrengthPresets>({
      queryKey: "presets/upscale",
    });

  const preset = generationParams.imagination_strength_preset;

  let creativity: number | null = null;
  let resemblance: number | null = null;

  if (preset === CUSTOM_UPSCALE_IMAGINATION_STRENGTH_PRESET) {
    creativity = generationParams.creativity;
    resemblance = generationParams.resemblance;
  } else if (upscaleImaginationStrengthPresets && preset) {
    const presetParams = upscaleImaginationStrengthPresets[preset];
    creativity = presetParams.creativity;
    resemblance = presetParams.resemblance;
  }

  return (
    <>
      {generationParams.imagination_strength_preset && (
        <TextItemSectionDetails
          name="Preset"
          value={generationParams.imagination_strength_preset}
        />
      )}
      {generationParams.scale_factor && (
        <TextItemSectionDetails
          name="Scale factor"
          value={generationParams.scale_factor + "x"}
        />
      )}
      {creativity !== null && (
        <TextItemSectionDetails
          name="Creativity"
          value={Math.round(creativity * 100) + "%"}
        />
      )}
      {resemblance !== null && (
        <TextItemSectionDetails
          name="Similarity"
          value={Math.round(resemblance * 100) + "%"}
        />
      )}
      <TextItemSectionDetails
        name="Exclude"
        value={generationParams.negative_prompt ?? "Nothing"}
      />
    </>
  );
};
